<template>
	<div class="cars-page">
		<CarsFiltersModalMobile />
		<div class="cars-page__container">
			<Transition name="fade">
				<div v-if="locationsBar && bookingStore.getBookerLocations?.start?.crmID" class="cars-page__bar">
					<CarsLocationReservationsBar :location="bookingStore.getBookerLocations.start" @close="locationsBar = false" />
				</div>
			</Transition>
			<div class="cars-page__heading">
				<h1 class="cars-page__title">{{ $t('common.cars') }}</h1>
			</div>
			<div class="cars-page__filters">
				<div class="cars-page__choose-filters">
					<CarsFilters />
				</div>
			</div>
			<div ref="carsListingRef" class="cars-page__main">
				<div ref="listingTopRef" class="cars-page__main-info">
					<span v-if="!carsStore.getCarsLoading">
						{{
							$t('pages.cars.showing_count', {
								current: carsStore.getFilteredCars?.length ?? 0,
								total: carsStore.getPagination?.total ?? carsStore.getFilteredCars?.length ?? 0,
							})
						}}
					</span>
					<!-- <Skeletor :height="24" :width="28" v-else /> -->
					<v-progress-circular v-else aria-label="loading" size="26"></v-progress-circular>
				</div>
				<Transition name="fade">
					<div v-if="carsStore.getCarsLoading" class="cars-page__cars-container">
						<Skeletor v-for="i in urlFilters.state.value.limit" :key="i" class="cars-page__cars-container-skeletor" round :height="552" />
					</div>
				</Transition>
				<Transition name="fade">
					<div v-if="!carsStore.getCarsLoading" class="cars-page__cars-container">
						<CarsSingleCar
							v-for="(car, i) in carsStore.getFilteredCars"
							:key="car.id"
							:car="car"
							:index="i"
							:loading="i < 3 ? 'eager' : undefined" />
					</div>
				</Transition>
				<ButtonPrimary
					v-if="carsStore.getPagination && carsStore.getPagination.totalPages > 1"
					class="cars-page__show-more"
					@click="onShowMore">
					{{ $t('pages.cars.show_more') }}
				</ButtonPrimary>
				<span v-if="carsStore.getPagination && carsStore.getPagination.totalPages > 1" class="cars-page__show-more-count">
					{{
						$t('pages.cars.showing_count', {
							current: carsStore.getFilteredCars?.length ?? 0,
							total: carsStore.getPagination.total,
						})
					}}
				</span>
				<v-pagination
					v-if="carsStore.getPagination && carsStore.getPagination.totalPages > 1"
					:model-value="carsStore.getPagination.currentPage"
					:length="carsStore.getPagination.totalPages"
					class="cars-page__pagination"
					@update:model-value="
						(page) => {
							urlFilters.setPage(page)
							scrollToListingTop()
						}
					">
					<template #prev="slotProps">
						<v-btn v-bind="slotProps" icon variant="text">
							<GlobalIcon name="arrow-left" :height="16" />
						</v-btn>
					</template>
					<template #next="slotProps">
						<v-btn v-bind="slotProps" icon variant="text">
							<GlobalIcon name="arrow-right" :height="16" />
						</v-btn>
					</template>
				</v-pagination>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { Skeletor } from 'vue-skeletor'
import { format } from 'date-fns'
import debounce from 'lodash/debounce'
import { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'
import { EFilterNames, CARS_URL_DEFAULTS, type ICarSelectedFilters } from '@/modules/carsModule/types/filters'

const cookie = useCookie('cf-car')
cookie.value = null
const isFiltersModalOpen = ref(false)
const step = inject<Ref<number>>('step')!
if (step) step.value = 0
const $t = useI18n().t
const carsStore = useCarsStore()
const locationsStore = useLocationsStore()
const bookingStore = useBookingStore()
const locationsBar = ref(true)
const config = useNuxtApp().$config
const route = useRoute()

// URL-driven filters
const urlFilters = useUrlFilters()
const bookerService = useBookerService()

// Scroll handling
const listingTopRef = ref<HTMLElement | null>(null)

const SCROLL_TOP_OFFSET = 100

function scrollToListingTop() {
	const el = listingTopRef.value
	if (!el) return
	const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_TOP_OFFSET
	window.scrollTo({ top, behavior: 'smooth' })
}

const lastCarOffset = ref<any>(null)
function onShowMore() {
	const cars = carsStore.getFilteredCars
	const carId = cars && cars.length > 0 ? cars[cars.length - 1].id : null
	const carElem = window.document?.querySelector<HTMLElement>(`[data-car-id="${carId}"]`)
	lastCarOffset.value = carElem?.offsetTop
	urlFilters.setLimit(urlFilters.state.value.limit + CARS_URL_DEFAULTS.limit)
}

watch(
	() => carsStore.getCarsLoading,
	(loading, wasLoading) => {
		if (loading || !wasLoading || lastCarOffset.value === null) return
		nextTick(() => {
			window.scrollTo(0, lastCarOffset.value)
		})
	},
)

function getDefaultDates(): { dateOut: string; dateIn: string } | null {
	const s = urlFilters.state.value
	if (s.dateOut && s.dateIn) return null

	if (initialDateOut && initialDateIn) return { dateOut: initialDateOut, dateIn: initialDateIn }

	return {
		dateOut: format(bookerService.getStartDate(), "yyyy-MM-dd'T'HH:mm"),
		dateIn: format(bookerService.getEndDate(), "yyyy-MM-dd'T'HH:mm"),
	}
}

function buildFetchParams(options?: { signal?: AbortSignal; skipSSRPayload?: boolean }) {
	const s = urlFilters.state.value
	return {
		signal: options?.signal,
		skipSSRPayload: options?.skipSSRPayload ?? false,
		searches: urlFilters.toApiSearchParams(),
		filters: urlFilters.toApiFilterParams(getDefaultDates()),
		sort: s.sort ?? undefined,
		page: s.page,
		perPage: s.limit,
	}
}

// Sync URL state → store state (for filter UI components)
function syncUrlToStore() {
	const s = urlFilters.state.value

	// Groups
	const groupModels = s.groups.map((name) => carsStore.getAllGroups?.find((g) => g.name === name)).filter((g): g is IProductGroupModel => g != null)
	carsStore.state.currentGroups = groupModels

	// Build filter array
	const filters: ICarSelectedFilters[] = []

	const transmissionParam = carsStore.getFilterByParameterName(EFilterNames.TRANSMISSION)
	if (s.transmission.length > 0 && transmissionParam) {
		filters.push({ parameter: transmissionParam, values: [...s.transmission] })
	}

	const fuelParam = carsStore.getFilterByParameterName(EFilterNames.FUEL_TYPE)
	if (s.fuelType.length > 0 && fuelParam) {
		filters.push({ parameter: fuelParam, values: [...s.fuelType] })
	}

	const seatsParam = carsStore.getFilterByParameterName(EFilterNames.SEATS)
	if (s.seatsMin != null && seatsParam) {
		filters.push({ parameter: seatsParam, values: [], min: s.seatsMin })
	}

	carsStore.state.currentFilters = filters

	// Price range
	if (s.priceMin != null || s.priceMax != null) {
		carsStore.state.currentPriceRange = {
			name: `${s.priceMin ?? 0}-${s.priceMax ?? ''}`,
			value: `${s.priceMin ?? 0}-${s.priceMax ?? ''}`,
			min: s.priceMin ?? 0,
			max: s.priceMax ?? undefined,
		}
	} else {
		carsStore.state.currentPriceRange = null
	}

	// Sorting
	if (s.sort) {
		carsStore.state.currentSorting = { name: s.sort, value: s.sort }
	} else {
		carsStore.state.currentSorting = null
	}
}

await Promise.all([carsStore.fetchAllFilters(), carsStore.fetchAllSegments(), carsStore.fetchAllGroups(), locationsStore.fetchLocations()])

// Before first fetch: determine the actual dates/location to use
// Priority: 1) URL params  2) bookingStore (sessionStorage)  3) defaults
let initialDateOut: string | null = null
let initialDateIn: string | null = null
let initialOfficeId: string | null = null
let initialDatesSource: EBookerDatesSource = EBookerDatesSource.USER
let needsUrlUpdate = false
let needsRefetch = false

{
	const urlState = urlFilters.state.value
	initialOfficeId = urlState.officeId

	const defaultDateOut = format(bookerService.getStartDate(), "yyyy-MM-dd'T'HH:mm")
	const defaultDateIn = format(bookerService.getEndDate(), "yyyy-MM-dd'T'HH:mm")

	const areUrlDatesValid =
		!!urlState.dateOut && !!urlState.dateIn && bookingStore.isDatesCorrectForRenteon(new Date(urlState.dateOut), new Date(urlState.dateIn))

	if (urlState.dateOut && urlState.dateIn && areUrlDatesValid) {
		initialDateOut = urlState.dateOut
		initialDateIn = urlState.dateIn
	} else if (urlState.dateOut && urlState.dateIn) {
		// URL has dates, but they are already too early for Renteon
		initialDateOut = defaultDateOut
		initialDateIn = defaultDateIn
		initialDatesSource = EBookerDatesSource.AUTO
		needsUrlUpdate = true
		needsRefetch = true
	} else if (process.client) {
		// Read directly from sessionStorage (bookingStore state may not be hydrated yet)
		const localDates = bookingStore.getLocalBookerDates

		if (localDates?.start && localDates?.end && bookingStore.isDatesCorrectForRenteon(new Date(localDates.start), new Date(localDates.end))) {
			initialDateOut = format(new Date(localDates.start), "yyyy-MM-dd'T'HH:mm")
			initialDateIn = format(new Date(localDates.end), "yyyy-MM-dd'T'HH:mm")

			initialDatesSource = localDates.source
			needsUrlUpdate = initialDatesSource === EBookerDatesSource.USER

			// Only refetch if session dates differ from SSR defaults
			if (initialDateOut !== defaultDateOut || initialDateIn !== defaultDateIn) {
				needsRefetch = true
			}
		} else {
			// Nothing picked yet → keep the clean, canonical URL instead of redirecting to default dates
			initialDateOut = defaultDateOut
			initialDateIn = defaultDateIn
			initialDatesSource = EBookerDatesSource.AUTO
		}
	} else {
		// SSR without URL params - use defaults
		initialDateOut = defaultDateOut
		initialDateIn = defaultDateIn
		initialDatesSource = EBookerDatesSource.AUTO
	}

	// Fill office from session if not in URL
	if (!initialOfficeId && process.client) {
		const localLocations = bookingStore.getLocalBookerLocations
		if (localLocations?.start?.crmID) {
			initialOfficeId = String(localLocations.start.crmID)
			needsUrlUpdate = true
			needsRefetch = true // office from session → always differs from SSR (which had no office)
		}
	}

	if (process.client) {
		const selectedCurrency = useCookie('currency').value || ECurrency.PLN
		if (selectedCurrency !== ECurrency.PLN) needsRefetch = true
	}

	// Sync resolved params → bookingStore (URL overwrites sessionStorage)
	if (process.client && initialDateOut && initialDateIn) {
		bookingStore.setBookerDates(
			{
				start: new Date(initialDateOut),
				end: new Date(initialDateIn),
			},
			initialDatesSource,
		)

		if (initialOfficeId) {
			const locations = locationsStore.getLocations
			const locationModel = locations?.find((l: any) => String(l.crmID) === initialOfficeId) ?? null
			if (locationModel) {
				bookingStore.setBookerLocations({
					start: locationModel,
					end: bookingStore.getBookerLocations?.end ?? null,
				})
			}
		}
	}
}

syncUrlToStore()

// Build initial fetch params using resolved dates (not from URL which may not be updated yet)
function buildInitialFetchParams() {
	const filters: IProductFilter[] = []
	if (initialDateOut) filters.push({ id: 'date_out', value: format(new Date(initialDateOut), 'dd-MM-yyyy HH:mm:ss') })
	if (initialDateIn) filters.push({ id: 'date_in', value: format(new Date(initialDateIn), 'dd-MM-yyyy HH:mm:ss') })
	if (initialOfficeId) filters.push({ id: 'office_id', value: initialOfficeId })
	filters.push({ id: 'has_price', value: '1' })

	const s = urlFilters.state.value
	return {
		skipSSRPayload: true,
		searches: urlFilters.toApiSearchParams(),
		filters,
		sort: s.sort ?? undefined,
		page: s.page,
		perPage: s.limit,
	}
}

await useAsyncData('cars-listing-initial', () => carsStore.fetchCarsWithFilters(buildInitialFetchParams()))

let currentAbortController: AbortController | null = null
let isInitialLoad = true

if (process.client) {
	const debouncedFetch = debounce(async () => {
		if (currentAbortController) {
			currentAbortController.abort()
		}
		currentAbortController = new AbortController()
		const signal = currentAbortController.signal

		carsStore.state.carsLoading = true

		try {
			await carsStore.fetchCarsWithFilters(buildFetchParams({ signal }))
		} catch (err: any) {
			if (err?.name !== 'AbortError' && !signal.aborted) {
				logger.error(err)
			}
			if (!signal.aborted) {
				carsStore.state.carsLoading = false
			}
		}
	}, 300)

	// Unified watcher: URL changed → sync UI + refetch
	watch(
		urlFilters.state,
		() => {
			syncUrlToStore()

			if (isInitialLoad) {
				isInitialLoad = false
				return
			}

			debouncedFetch()
		},
		{ deep: true },
	)

	// Sync resolved dates/location to URL (after watcher is registered)
	if (needsUrlUpdate) {
		if (needsRefetch) {
			// Session differs from SSR defaults → update URL and refetch
			isInitialLoad = false
		}
		// else: session == defaults → URL updates but watcher skips fetch via isInitialLoad
		// Auto-filled dates stay out of the URL even when the office has to be written into it
		const isUserPickedDates = initialDatesSource === EBookerDatesSource.USER
		urlFilters.setDateAndLocation(isUserPickedDates ? initialDateOut : null, isUserPickedDates ? initialDateIn : null, initialOfficeId, true)
	}

	if (!needsUrlUpdate) {
		nextTick(() => {
			isInitialLoad = false
		})

		// URL stays untouched, so the watcher above won't fire - but SSR rendered the listing for its own
		// default dates, so the data has to be refreshed for the dates we actually resolved
		if (needsRefetch) debouncedFetch()
	}
}

// Analytics
const carsListingRef = ref<HTMLElement | null>(null)
useSearchListingViewed(carsListingRef, toRef(carsStore, 'getFilteredCars'), 'Listing samochodów')

usePageViewed(EGTMPageType.CARS)

onMounted(() => {
	window.scrollTo(0, 0)
})

provide('isFiltersModalOpen', isFiltersModalOpen)
provide('urlFilters', urlFilters)

definePageMeta(pageMeta.RESERVATION.CARS)

const metaRobots = computed(() => {
	if (config.public.isIndex) {
		return 'index, follow'
	} else {
		return 'noindex, nofollow'
	}
})

function buildPageUrl(page: number): string {
	const query = { ...route.query } as Record<string, string>
	if (page <= 1) {
		delete query.page
	} else {
		query.page = String(page)
	}
	const qs = new URLSearchParams(query).toString()
	return config.public.baseURL + route.path + (qs ? '?' + qs : '')
}

function buildCanonicalUrl(page: number): string {
	return config.public.baseURL + route.path + (page > 1 ? `?page=${page}` : '')
}

useHead(() => {
	const currentPage = urlFilters.state.value.page
	const pagination = carsStore.getPagination
	const links: { rel: string; href: string }[] = [{ rel: 'canonical', href: buildCanonicalUrl(currentPage) }]

	if (pagination && currentPage > 1) {
		links.push({ rel: 'prev', href: buildPageUrl(currentPage - 1) })
	}
	if (pagination && currentPage < pagination.totalPages) {
		links.push({ rel: 'next', href: buildPageUrl(currentPage + 1) })
	}

	return {
		title: $t('pages.cars.meta_title') + ' | CarFree',
		link: links,
		meta: [
			{ name: 'robots', content: metaRobots.value },
			{ name: 'description', content: $t('common.cars_description') },
			{ property: 'og:title', content: $t('pages.cars.meta_title') + ' | CarFree' },
			{ property: 'og:description', content: $t('common.cars_description') },
		],
	}
})
</script>

<style lang="scss" scoped>
@import 'vue-skeletor/dist/vue-skeletor.css';
</style>
<style lang="scss" scoped src="./cars.scss"></style>
